/**
 * Documents on records (P23) + the person photo (P24, kind PHOTO) — migration 0104.
 *
 * Backend router: prefix /documents.
 *   - GET    /kinds                              the document types, with where each fits
 *   - GET    /?owner_type=&owner_id=             a record's documents + its pending uploads
 *   - POST   /                     (multipart)   upload. Admin / Compliance: applied;
 *                                                RM: filed for approval
 *   - GET    /{id}/file[?inline=true]            the file (download, or view in a new tab)
 *   - GET    /pending/{cr_id}/file[?inline=true] a pending upload, for its requester + approvers
 *   - DELETE /{id}                               remove (soft; an RM's is filed for approval)
 *   - POST   /{id}/restore                       undo a remove (same rule)
 *
 * Writes need `record.write`. Every list and file read is logged (DLP). The multipart
 * upload uses a raw same-origin fetch (the JSON client only speaks JSON), as
 * presentations.ts does, but throws ApiError so callers read `detail` the usual way.
 */
import { ApiError, api } from './client';
import { downloadBlob } from './iaaExports';
import type { ISODateTime, PendingChangeRequestRef, UUID } from './types';

export type DocumentOwnerType = 'agreements' | 'persons' | 'accounts' | 'insurance_policies';

/** Same rules as the server (`document_store`): PDF / JPG / PNG, at most 10 MB. */
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';

export interface DocumentKindSpec {
  kind: string;
  label: string;
  owner_types: DocumentOwnerType[];
  person_kinds: ('NATURAL' | 'LEGAL')[] | null;
  images_only: boolean;
}

export interface RecordDocument {
  id: UUID;
  owner_type: DocumentOwnerType;
  owner_id: UUID;
  kind: string;
  kind_label: string;
  title: string | null;
  notes: string | null;
  filename: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  created_at: ISODateTime | null;
  created_by_name: string | null;
  deleted_at: ISODateTime | null;
  pending_change_requests: PendingChangeRequestRef[];
}

export interface PendingUpload {
  change_request_id: UUID;
  kind: string;
  kind_label: string;
  title: string | null;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  requested_by: UUID;
  requested_by_name: string | null;
  requested_at: ISODateTime | null;
  op_label: string;
}

export interface RecordDocuments {
  documents: RecordDocument[];
  pending_uploads: PendingUpload[];
}

export interface DocumentWriteResult {
  pending: boolean;
  change_request_id: UUID;
}

export interface DocumentUploadInput {
  owner_type: DocumentOwnerType;
  owner_id: UUID;
  kind: string;
  title?: string | null;
  file: File;
}

async function apiErrorOf(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null;
  try { body = await res.json(); } catch { /* non-JSON body */ }
  const detail = (body as { detail?: unknown } | null)?.detail;
  return new ApiError(res.status, typeof detail === 'string' ? detail : fallback, null, body);
}

/** Pull the file name from a Content-Disposition header (RFC 5987 first). */
function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

export const documentsApi = {
  kinds: (): Promise<DocumentKindSpec[]> => api.get<DocumentKindSpec[]>('/documents/kinds'),

  list: (ownerType: DocumentOwnerType, ownerId: UUID, includeDeleted = false): Promise<RecordDocuments> =>
    api.get<RecordDocuments>(
      `/documents?owner_type=${ownerType}&owner_id=${ownerId}${includeDeleted ? '&include_deleted=true' : ''}`,
    ),

  async upload(input: DocumentUploadInput): Promise<DocumentWriteResult> {
    const form = new FormData();
    form.append('owner_type', input.owner_type);
    form.append('owner_id', input.owner_id);
    form.append('kind', input.kind);
    if (input.title) form.append('title', input.title);
    form.append('file', input.file);
    const res = await fetch('/documents', { method: 'POST', credentials: 'include', body: form });
    if (!res.ok) throw await apiErrorOf(res, 'The file could not be uploaded.');
    return res.json();
  },

  remove: (id: UUID): Promise<DocumentWriteResult> => api.delete<DocumentWriteResult>(`/documents/${id}`),

  restore: (id: UUID): Promise<DocumentWriteResult> =>
    api.post<DocumentWriteResult>(`/documents/${id}/restore`, {}),

  /** Open a file in a new browser tab (PDFs and images display; C-260: never embedded). */
  view(id: UUID): void {
    window.open(`/documents/${id}/file?inline=true`, '_blank', 'noopener');
  },

  viewPending(changeRequestId: UUID): void {
    window.open(`/documents/pending/${changeRequestId}/file?inline=true`, '_blank', 'noopener');
  },

  /** Save a file to disk under its own name. */
  async download(id: UUID, fallbackName = 'document'): Promise<void> {
    const res = await fetch(`/documents/${id}/file`, { credentials: 'include' });
    if (!res.ok) throw await apiErrorOf(res, 'The file could not be downloaded.');
    downloadBlob(await res.blob(), fileNameFromCD(res.headers.get('Content-Disposition') ?? '', fallbackName));
  },
};

/** "1.2 MB" / "340 KB". */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The same checks the server makes, so a wrong file is refused before Save. */
export function checkDocumentFile(file: File, imagesOnly: boolean): string | null {
  if (file.size === 0) return 'The file is empty.';
  if (file.size > DOCUMENT_MAX_BYTES) {
    return `This file is over the 10 MB limit (${(file.size / 1024 / 1024).toFixed(2)} MB).`;
  }
  const name = file.name.toLowerCase();
  const isImage = /\.(jpe?g|png)$/.test(name) || file.type === 'image/jpeg' || file.type === 'image/png';
  const isPdf = name.endsWith('.pdf') || file.type === 'application/pdf';
  if (imagesOnly && !isImage) return 'This must be a JPG or PNG image.';
  if (!isImage && !isPdf) return 'Only PDF, JPG and PNG files can be uploaded.';
  return null;
}
